/**
 * ShotPanelRenderer — world-space near-tank active shot label above the current player.
 *
 * The old screen-space card panel has been removed (weapon info moved to PlayerCards).
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { SHOT_TIER, Z } from '../../constants';
import type { GameState } from '../../types';
import { getTankShot } from '../../tanks';
import { modeUsesShotTiers } from '../../game/mode';

export class ShotPanelRenderer {
  // Near-tank active shot label (world-space, above current player)
  private nearTankShotLabel: Text;

  // Shot switch animation state
  private prevSelectedTier: number = 1;
  private nearTankFlashTimer: number = 0;

  constructor(_uiContainer: Container, worldContainer: Container) {
    // --- Near-tank active shot label (world-space) ---
    this.nearTankShotLabel = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: SHOT_TIER.NEAR_TANK_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.nearTankShotLabel.anchor.set(0.5, 0.5);
    this.nearTankShotLabel.zIndex = Z.TANKS + 3;
    this.nearTankShotLabel.visible = false;
    worldContainer.addChild(this.nearTankShotLabel);
  }

  /** Hide near-tank label (e.g. during menu phases). */
  hide(): void {
    this.nearTankShotLabel.visible = false;
  }

  update(state: GameState, dt: number): void {
    const curPlayer = state.players[state.currentPlayer];
    const showShotTier = curPlayer && curPlayer.alive
      && state.phase === 'aiming'
      && modeUsesShotTiers(state.modeState.mode);

    if (showShotTier) {
      this.nearTankShotLabel.visible = true;

      // --- Shot switch detection ---
      if (curPlayer.selectedShotTier !== this.prevSelectedTier) {
        this.nearTankFlashTimer = SHOT_TIER.NEAR_TANK_FLASH;
        this.prevSelectedTier = curPlayer.selectedShotTier;
      }

      // Tick animation timer
      if (this.nearTankFlashTimer > 0) this.nearTankFlashTimer = Math.max(0, this.nearTankFlashTimer - dt);

      // --- Near-tank active shot label (world-space) ---
      const selectedShot = getTankShot(curPlayer.tankId, curPlayer.selectedShotTier, curPlayer.terraformerMode);
      this.nearTankShotLabel.text = selectedShot.label.toUpperCase();
      this.nearTankShotLabel.style.fill = selectedShot.tierColor;
      this.nearTankShotLabel.position.set(curPlayer.x, curPlayer.y + SHOT_TIER.INDICATOR_Y_OFFSET);

      // Near-tank flash animation
      const ntFlashT = this.nearTankFlashTimer > 0 ? this.nearTankFlashTimer / SHOT_TIER.NEAR_TANK_FLASH : 0;
      this.nearTankShotLabel.alpha = SHOT_TIER.NEAR_TANK_ALPHA + (1 - SHOT_TIER.NEAR_TANK_ALPHA) * ntFlashT;
      const ntScale = 1 + (SHOT_TIER.NEAR_TANK_SCALE_POP - 1) * ntFlashT;
      this.nearTankShotLabel.scale.set(ntScale);
    } else {
      this.nearTankShotLabel.visible = false;
      // Reset switch detection state when label hidden
      this.prevSelectedTier = 1;
    }
  }

  destroy(): void {
    this.nearTankShotLabel.destroy();
  }
}
